import React, { useEffect, useState } from 'react';
import { useSearchParams, Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Check, Loader2, Copy, CheckCircle, MapPin, Clock, Phone, MessageCircle, AlertTriangle } from 'lucide-react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const LOCKER_LABELS = {
  small: 'S - Small',
  medium: 'M - Medium',
  xlarge: 'XL - Extra Large'
};

const SIZE_FOR_LOCKER = {
  small: 'Small',
  medium: 'Medium',
  xlarge: 'Large'
};

// Google Ads Conversion Tracking
const trackGoogleAdsConversion = (value, transactionId) => {
  if (typeof window !== 'undefined' && window.gtag) {
    window.gtag('event', 'conversion', {
      'send_to': 'AW-18176840966/purchase',
      'value': value,
      'currency': 'EUR',
      'transaction_id': transactionId
    });
    // Google Ads conversion tracked
  }
};

const BookingSuccess = () => {
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  
  const [loading, setLoading] = useState(true);
  const [booking, setBooking] = useState(null);
  const [error, setError] = useState(null);
  const [copied, setCopied] = useState(false);
  const [pollAttempts, setPollAttempts] = useState(0);
  const [conversionTracked, setConversionTracked] = useState(false);

  useEffect(() => {
    if (!sessionId) {
      setError('Sessione di pagamento non trovata');
      setLoading(false);
      return;
    }

    const pollPaymentStatus = async () => {
      try {
        const response = await axios.get(`${API_URL}/api/locker/payment-status/${sessionId}`);
        
        if (response.data.payment_status === 'paid') {
          setBooking(response.data.booking);
          setLoading(false);
          
          // Track Google Ads conversion (only once)
          if (!conversionTracked && response.data.booking) {
            trackGoogleAdsConversion(
              response.data.booking.total_price,
              response.data.booking.booking_code
            );
            setConversionTracked(true);
          }
        } else if (response.data.status === 'expired') {
          setError('La sessione di pagamento è scaduta. Riprova.');
          setLoading(false);
        } else if (pollAttempts < 10) {
          // Continue polling
          setTimeout(() => setPollAttempts(prev => prev + 1), 2000);
        } else {
          setError('Timeout nel verificare il pagamento. Contattaci se il pagamento è stato addebitato.');
          setLoading(false);
        }
      } catch (err) {
        // Payment status check failed - retry or show error
        if (pollAttempts < 3) {
          setTimeout(() => setPollAttempts(prev => prev + 1), 2000);
        } else {
          setError('Errore nel verificare il pagamento. Contattaci per assistenza.');
          setLoading(false);
        }
      }
    };

    pollPaymentStatus();
  }, [sessionId, pollAttempts, conversionTracked]);

  const copyBookingCode = () => {
    const codeToCopy = booking?.unlock_code || booking?.booking_code;
    if (codeToCopy) {
      navigator.clipboard.writeText(codeToCopy);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center"
        >
          <Loader2 className="w-16 h-16 text-[#FFCC00] animate-spin mx-auto mb-6" />
          <h2 className="text-white text-2xl font-bold mb-2">Verifica pagamento in corso...</h2>
          <p className="text-gray-400">Attendere prego, non chiudere questa pagina.</p>
        </motion.div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="max-w-md w-full text-center"
        >
          <div className="w-20 h-20 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-6">
            <span className="text-4xl">⚠️</span>
          </div>
          <h2 className="text-white text-2xl font-bold mb-4">Si è verificato un errore</h2>
          <p className="text-gray-400 mb-8">{error}</p>
          <div className="flex flex-col gap-4">
            <Link
              to="/"
              className="bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide hover:bg-[#E6B800] px-8 py-4 transition-all"
            >
              Torna alla Home
            </Link>
            <a
              href="https://wa.me/393773781602?text=Ciao, ho avuto un problema con il pagamento"
              target="_blank"
              rel="noopener noreferrer"
              className="flex items-center justify-center gap-2 text-[#FFCC00] hover:underline"
            >
              <MessageCircle className="w-5 h-5" />
              Contattaci su WhatsApp
            </a>
          </div>
        </motion.div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0A0A0A] flex items-center justify-center px-4 py-20">
      <motion.div
        initial={{ opacity: 0, y: 20 }}
        animate={{ opacity: 1, y: 0 }}
        className="max-w-lg w-full"
      >
        {/* Success Icon */}
        <div className="text-center mb-8">
          <motion.div
            initial={{ scale: 0 }}
            animate={{ scale: 1 }}
            transition={{ type: "spring", delay: 0.2 }}
            className="w-24 h-24 bg-[#FFCC00] rounded-full flex items-center justify-center mx-auto mb-6"
          >
            <Check className="w-12 h-12 text-[#0A0A0A]" strokeWidth={3} />
          </motion.div>
          <h1 className="font-heading text-3xl md:text-4xl font-bold text-white mb-2">
            Pagamento Completato!
          </h1>
          <p className="text-gray-400">La tua prenotazione è stata confermata.</p>
        </div>

        {/* Promo Code - Main highlight */}
        <div className="bg-[#141414] border-2 border-[#FFCC00] p-6 mb-6">
          <p className="text-gray-400 text-sm text-center mb-2">Il tuo CODICE PROMO (sconto 100%):</p>
          <div className="flex items-center justify-center gap-4">
            <span className="text-[#FFCC00] text-4xl font-mono font-bold tracking-wider">
              {booking?.unlock_code || booking?.booking_code}
            </span>
            <button
              onClick={copyBookingCode}
              className="p-2 text-gray-400 hover:text-[#FFCC00] transition-colors"
              title="Copia codice"
            >
              {copied ? <CheckCircle className="w-6 h-6 text-green-500" /> : <Copy className="w-6 h-6" />}
            </button>
          </div>
          <p className="text-gray-500 text-xs text-center mt-2">
            Usa questo codice al locker per avere lo sconto del 100% (hai già pagato online)
          </p>
        </div>

        {/* Step by Step Instructions */}
        <div className="bg-[#141414] border-2 border-[#FFCC00] p-6 mb-6">
          <h3 className="text-[#FFCC00] font-bold mb-4 text-lg">COME DEPOSITARE - Segui questi passi:</h3>
          <ol className="space-y-3 text-sm">
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">1</span>
              <span className="text-gray-300">Vai al punto <strong className="text-white">Unlock Bags</strong> in Via Ernesto Capocci 6</span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">2</span>
              <span className="text-gray-300">Sul touch screen, premi <strong className="text-[#FFCC00]">DEPOSIT</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">3</span>
              <span className="text-gray-300">Seleziona la durata: <strong className="text-[#FFCC00]">{booking?.hours} ora/e</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">4</span>
              <span className="text-gray-300">Seleziona la dimensione: <strong className="text-[#FFCC00]">{SIZE_FOR_LOCKER[booking?.locker_size] || 'Medium'}</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">5</span>
              <span className="text-gray-300">Quando chiede il pagamento, inserisci il <strong className="text-[#FFCC00]">CODICE PROMO: {booking?.unlock_code || booking?.booking_code}</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">6</span>
              <span className="text-gray-300">Crea il <strong className="text-[#FFCC00]">TUO PIN personale</strong> (6 cifre a tua scelta)</span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">7</span>
              <span className="text-gray-300">Inserisci il tuo numero di telefono: <strong className="text-[#FFCC00]">{booking?.phone}</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">8</span>
              <span className="text-gray-300">Il locker si aprirà - <strong className="text-white">deposita i bagagli e chiudi</strong></span>
            </li>
            <li className="flex gap-3">
              <span className="bg-[#FFCC00] text-[#0A0A0A] w-6 h-6 rounded-full flex items-center justify-center font-bold shrink-0">9</span>
              <span className="text-gray-300">Per ritirare, usa il <strong className="text-[#FFCC00]">PIN che hai creato</strong> + telefono</span>
            </li>
          </ol>
        </div>

        {/* Important Warning */}
        <div className="bg-red-900/20 border border-red-500/50 p-4 mb-6">
          <div className="flex gap-3">
            <AlertTriangle className="w-5 h-5 text-red-400 shrink-0 mt-0.5" />
            <div>
              <p className="text-red-400 font-semibold text-sm">IMPORTANTE</p>
              <p className="text-red-300 text-sm">Il PIN lo scegli TU al momento del deposito. Scrivilo o ricordalo bene - ti servirà per ritirare i bagagli!</p>
            </div>
          </div>
        </div>

        {/* Booking Details */}
        <div className="bg-[#141414] border border-[#2A2A2A] p-6 mb-6">
          <h3 className="text-white font-semibold mb-4">Dettagli Prenotazione</h3>
          <div className="space-y-3 text-sm">
            <div className="flex justify-between">
              <span className="text-gray-400">📦 Locker:</span>
              <span className="text-white font-medium">{LOCKER_LABELS[booking?.locker_size]}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">📅 Data:</span>
              <span className="text-white font-medium">{booking?.date}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">⏰ Orario:</span>
              <span className="text-white font-medium">{booking?.start_time} - {booking?.end_time}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">⏱️ Durata:</span>
              <span className="text-white font-medium">{booking?.hours} {booking?.hours === 1 ? 'ora' : 'ore'}</span>
            </div>
            <div className="border-t border-[#2A2A2A] pt-3 flex justify-between">
              <span className="text-gray-400 font-semibold">💰 Totale Pagato:</span>
              <span className="text-[#FFCC00] font-bold text-lg">€{booking?.total_price?.toFixed(2).replace('.', ',')}</span>
            </div>
          </div>
        </div>

        {/* Location Info */}
        <div className="bg-[#141414] border border-[#2A2A2A] p-6 mb-6">
          <h3 className="text-white font-semibold mb-4 flex items-center gap-2">
            <MapPin className="w-5 h-5 text-[#FFCC00]" />
            Dove Trovarci
          </h3>
          <p className="text-white">Via Ernesto Capocci 6</p>
          <p className="text-gray-400">80133 Napoli</p>
          <div className="flex items-center gap-2 mt-3 text-gray-400">
            <Clock className="w-4 h-4 text-[#FFCC00]" />
            <span>Orari: 6:00 - 23:00</span>
          </div>
          <a
            href="https://maps.google.com/?q=Via+Ernesto+Capocci+6+Napoli+80133"
            target="_blank"
            rel="noopener noreferrer"
            className="inline-block mt-4 text-[#FFCC00] hover:underline text-sm"
          >
            Apri in Google Maps →
          </a>
        </div>

        {/* Actions */}
        <div className="flex flex-col gap-4">
          <Link
            to="/"
            className="bg-[#FFCC00] text-[#0A0A0A] font-bold uppercase tracking-wide hover:bg-[#E6B800] px-8 py-4 text-center transition-all"
          >
            Torna alla Home
          </Link>
          <a
            href="https://wa.me/393773781602?text=Ciao, ho appena prenotato un locker"
            target="_blank"
            rel="noopener noreferrer"
            className="flex items-center justify-center gap-2 border border-[#2A2A2A] hover:border-[#25D366] text-white hover:text-[#25D366] px-8 py-4 transition-all"
          >
            <MessageCircle className="w-5 h-5" />
            Contattaci su WhatsApp
          </a>
        </div>

        {/* Email Notice */}
        <p className="text-gray-500 text-xs text-center mt-6">
          Abbiamo inviato i dettagli della prenotazione a <span className="text-gray-400">{booking?.email}</span>
        </p>
      </motion.div>
    </div>
  );
};

export default BookingSuccess;
